import { Box, IconButton, Tooltip } from "@mui/material"
import { MoreHorizOutlined, VisibilityOutlined } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import React, { useMemo } from "react"
import type { ReactElement } from "react"

import Chip from "@/components/common/Chip/Chip"
import LimitedSizeChipGroup from "@/components/common/Chip/LimitedSizeChipGroup"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { CompanyStatus, ContactSortField } from "@/graphql/codegen/graphql"
import type { Contact, ContactSubscription } from "@/graphql/codegen/graphql"
import { COMPANY_DEPARTMENT_LABEL_MAP } from "@/screens/Companies/constants/companyDepartmentDataMap"
import { createStatusMap } from "@/utils/statusMap"

export const companyStatusMap = createStatusMap<
  CompanyStatus,
  "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning"
>([
  [CompanyStatus.Active, "success"],
  [CompanyStatus.Pending, "warning"],
  [CompanyStatus.Rejected, "error"],
  [CompanyStatus.Inactive, "secondary"],
])

export type SortableAccessorKey = "companyName" | "contactName" | "contactSubscriptions" | "companyDepartment"

export const accessorKeyToContactSortField = new Map<SortableAccessorKey, ContactSortField>([
  ["companyName", ContactSortField.CompanyName],
  ["contactName", ContactSortField.ContactName],
  ["contactSubscriptions", ContactSortField.Subscriptions],
  ["companyDepartment", ContactSortField.Department],
])

export const contactSortFieldToAccessoryKey = new Map<ContactSortField, SortableAccessorKey>([
  [ContactSortField.CompanyName, "companyName"],
  [ContactSortField.ContactName, "contactName"],
  [ContactSortField.Subscriptions, "contactSubscriptions"],
  [ContactSortField.Department, "companyDepartment"],
])

const MoreChip = (hiddenChipsCount: number): ReactElement => (
  <Tooltip title={`${hiddenChipsCount} more items hidden`}>
    <Chip icon={<MoreHorizOutlined />} />
  </Tooltip>
)

const SubscriptionCell: React.FC<{ types: ContactSubscription[] }> = ({ types }) => {
  const chips = useMemo(() => types.map((type, index) => ({ id: `${type}-${index}`, name: type.name })), [types])

  return (
    <LimitedSizeChipGroup
      chips={chips}
      maxSize={400}
      renderChip={(chip) => (
        <Chip
          className='mr-2 rounded bg-tertiary-yellow-300'
          key={chip.id}
          label={chip.name}
          icon={chip?.icon}
          color={"warning"}
          style={{ maxWidth: chip?.maxWidth ?? "auto" }}
        />
      )}
      renderMoreChip={MoreChip}
    />
  )
}

export const columns: ColumnDef<Contact>[] = [
  {
    id: "companyName",
    accessorKey: "company.name",
    header: "Company",
    sortingFn: "alphanumeric",
    cell: ({ row }: CellContext<Contact, unknown>): ReactElement => {
      return (
        <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
          <StatusIndicator status={row.original.company?.status ?? CompanyStatus.Inactive} map={companyStatusMap} />
          <div>{row.original.company?.name}</div>
        </Box>
      )
    },
    enableSorting: true,
  },
  {
    id: "contactName",
    accessorKey: "contactName",
    header: "Contact",
    cell: ({ row }: CellContext<Contact, unknown>): ReactElement => {
      const { firstName, lastName, isActive } = row.original
      return (
        <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
          <StatusIndicator status={isActive ? CompanyStatus.Active : CompanyStatus.Inactive} map={companyStatusMap} />
          <div>
            {firstName} {lastName}
          </div>
        </Box>
      )
    },
    enableSorting: true,
  },
  {
    id: "companyDepartment",
    accessorKey: "companyDepartment",
    header: "Department",
    cell: ({ row }: CellContext<Contact, unknown>): ReactElement => {
      const companyDepartment = row.original.companyDepartment

      return (
        <Box className='ml-3 flex items-center'>
          {companyDepartment ? COMPANY_DEPARTMENT_LABEL_MAP[companyDepartment] : "-"}
        </Box>
      )
    },
    enableSorting: true,
  },
  {
    id: "contactSubscriptions",
    accessorKey: "contactSubscriptions",
    header: "Subscriptions",
    cell: ({ row }: CellContext<Contact, unknown>): ReactElement => (
      <SubscriptionCell types={row.original.contactSubscriptions.filter(Boolean) as ContactSubscription[]} />
    ),
    enableSorting: true,
  },
  {
    id: "actions",
    accessorKey: "",
    header: "",
    cell: (): ReactElement => (
      <div className='flex justify-end'>
        <IconButton>
          <VisibilityOutlined />
        </IconButton>
      </div>
    ),
    enableSorting: false,
    size: 48,
  },
]
